import { authenticatedBitswanBackendInstance } from '@/lib/api-client';

export type CertificationCall = {
  id: string;
  status: 'requested' | 'scheduled' | 'held' | 'cancelled';
  status_display: string;
  preferred_times: string;
  notes: string;
  scheduled_at: string | null;
  meeting_url: string;
  host_email: string;
  requested_by_email: string;
  created_at: string;
};

export type CertificationModule = {
  slug: string;
  title: string;
  summary: string;
  body: string;
  video_url: string;
  duration_minutes: number;
  pass_mark: number;
  is_required: boolean;
  questions: Array<{ question: string; options: string[] }>;
  watched: boolean;
  attempts: number;
  best_score: number;
  last_score: number;
  passed: boolean;
  passed_at: string | null;
};

export type MyCertification = {
  has_profile: boolean;
  certification_level: string;
  certification_display?: string;
  is_certified: boolean;
  is_published?: boolean;
  is_listed: boolean;
  certified_at?: string | null;
  call: CertificationCall | null;
  calls: CertificationCall[];
  modules: CertificationModule[];
  required_total: number;
  required_passed: number;
  training_complete: boolean;
  can_book_call: boolean;
};

export async function fetchMyCertification() {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.get('/advisor/certification/');
  return res.data as MyCertification;
}

export async function bookCertificationCall(input: {
  preferred_times: string;
  notes?: string;
}) {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.post('/advisor/certification/', input);
  return res.data as MyCertification & { call: CertificationCall };
}

export async function createAdvisorOrganization(name: string) {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.post('/advisor/organizations/', { name });
  return res.data as { id: string; display_name: string };
}

export async function createAdvisorProfile(
  input: Record<string, string | number>,
  orgId?: string,
) {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.post('/advisor/profile/', input, {
    headers: orgId ? { 'X-Org-Id': orgId } : undefined,
  });
  return res.data as { id: string; display_name: string };
}

export async function markModuleWatched(slug: string) {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.post(`/advisor/certification/modules/${slug}/watched/`);
  return res.data as MyCertification;
}

export async function submitModuleAnswers(slug: string, answers: number[]) {
  const api = await authenticatedBitswanBackendInstance();
  const res = await api.post(
    `/advisor/certification/modules/${slug}/answers/`,
    { answers },
  );
  return res.data as MyCertification & {
    score: number;
    passed: boolean;
    pass_mark: number;
  };
}
